import {
  createContext,
  FocusEvent,
  forwardRef,
  KeyboardEvent,
  ReactNode,
  Ref,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Popover,
  PopoverContent,
  PopoverPortal,
  PopoverTrigger,
} from '@radix-ui/react-popover';
import composeRefs from '@seznam/compose-react-refs';
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
  CommandLoading,
  defaultFilter,
} from 'cmdk';
import { Check, ChevronsUpDown, PlusIcon, SearchIcon } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';

import { useFormField } from '@components/primitives/FormField';
import { cn, easeOutSmooth } from '@components/primitives/utilities';

import css from './styles.module.css';

interface ComboboxContextValue<T> {
  value: T | undefined;
  onValueChange: (value: T) => void;
  getOption: (item: T) => ComboboxOption;
  close: () => void;
}

const ComboboxContext = createContext<ComboboxContextValue<any> | undefined>(
  undefined
);

function useComboboxContext<T>() {
  const context = useContext(ComboboxContext);
  if (!context) {
    throw new Error('useComboboxContext must be used within a Combobox');
  }
  return context as any as ComboboxContextValue<T>;
}

export interface ComboboxOption {
  value: string | number;
  keywords?: string[];
  icon?: ReactNode;
  label?: ReactNode;
  description?: ReactNode;
}

type ItemLabelProps = ComboboxOption;

function ItemLabel({ icon, label, value, description }: ItemLabelProps) {
  return (
    <div className={css.label}>
      <span className={css.icon} aria-hidden>
        {icon}
      </span>

      <span className={css.text}>
        <span className={css.title}>{label ?? value}</span>
        {description && <span className={css.description}>{description}</span>}
      </span>
    </div>
  );
}

interface ComboboxItemProps<T> {
  data: T;
}

function ComboboxItem<T>({ data }: ComboboxItemProps<T>) {
  const context = useComboboxContext<T>();
  const option = context.getOption(data);

  const handleSelect = useCallback(() => {
    context.onValueChange(data);
  }, [context, data]);

  return (
    <CommandItem
      className={css.item}
      value={option.value.toString()}
      onSelect={handleSelect}
      keywords={option.keywords}
    >
      <ItemLabel {...option} />

      <span className={css.check} aria-hidden>
        {context.value &&
          context.getOption(context.value).value === option.value && (
            <Check aria-hidden />
          )}
      </span>
    </CommandItem>
  );
}

interface ComboboxContentProps<T> {
  search: string;
  onSearchChange: (search: string) => void;
  shouldFilter?: boolean;
  items: T[];
  isLoadingItems?: boolean;
  allowCreate?: { label?: string; onCreate(search: string): void };
}

function ComboboxContent<T>({
  search,
  onSearchChange,
  shouldFilter = true,
  items,
  isLoadingItems,
  allowCreate,
}: ComboboxContentProps<T>) {
  const listRef = useRef<HTMLDivElement>(null);
  const context = useComboboxContext<T>();

  const customFilter = useCallback(
    (value: string, search: string, keywords?: string[]) => {
      if (allowCreate && value === '__new') {
        return 0.000000000001;
      }
      return defaultFilter(value, search, keywords);
    },
    [allowCreate]
  );

  const handleSearchChange = useCallback(
    (search: string) => {
      onSearchChange(search);
      listRef.current?.scrollTo({ top: 0, behavior: 'instant' });
    },
    [onSearchChange]
  );

  return (
    <Command
      className={css.command}
      loop
      shouldFilter={shouldFilter}
      filter={customFilter}
    >
      <div className={css.search}>
        <SearchIcon aria-hidden />
        <CommandInput
          value={search}
          onValueChange={handleSearchChange}
          placeholder="Search"
        />
      </div>

      {isLoadingItems && <CommandLoading className={css.loading} />}

      <div className={css.commandContent}>
        <CommandList ref={listRef} className={css.list}>
          {items.map((item) => (
            <ComboboxItem key={context.getOption(item).value} data={item} />
          ))}

          {allowCreate && (
            <CommandItem
              className={css.item}
              value="__new"
              keywords={[search]}
              onSelect={() => {
                context.close();
                allowCreate.onCreate(search);
              }}
            >
              <ItemLabel
                icon={<PlusIcon aria-hidden />}
                value="__new"
                label={allowCreate.label ?? 'Create new'}
              />
            </CommandItem>
          )}
        </CommandList>

        <CommandEmpty className={css.empty}>
          {search.length > 0 && !isLoadingItems && (
            <span className={css.emptyMessage}>No options</span>
          )}
        </CommandEmpty>
      </div>
    </Command>
  );
}

export interface ComboboxProps<T> {
  value: T | undefined;
  onValueChange: (value: T) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  shouldFilter?: boolean;
  search?: string;
  onSearchChange?: (search: string) => void;
  items: NonNullable<T>[];
  getOption: (item: NonNullable<T>) => ComboboxOption;
  onBlur?(event: FocusEvent<HTMLButtonElement>): void;
  name?: string;
  id?: string;
  isLoadingItems?: boolean;
  allowCreate?: { label?: string; onCreate(search: string): void };
}

export const Combobox = forwardRef<HTMLButtonElement, ComboboxProps<any>>(
  function Combobox(
    {
      value,
      onValueChange,
      placeholder,
      className,
      disabled,
      shouldFilter,
      isLoadingItems,
      items,
      getOption,
      onBlur,
      name,
      id,
      allowCreate,
      ...props
    },
    ref
  ) {
    const triggerRef = useRef<HTMLButtonElement>(null);
    const field = useFormField();

    const searchState = useState('');
    const search = props.search ?? searchState[0];
    const onSearchChange = props.onSearchChange ?? searchState[1];

    const [open, setOpen] = useState(false);
    const onOpenChange = useCallback((open: boolean) => {
      setOpen(open);
      if (!open) {
        onSearchChange('');
      }
    }, []);

    const context = useMemo<ComboboxContextValue<any>>(
      () => ({
        value,
        onValueChange: (value) => {
          onValueChange(value);
          onOpenChange(false);
        },
        close: () => onOpenChange(false),
        getOption,
      }),
      [value, onValueChange, onOpenChange, getOption]
    );

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLButtonElement>) => {
        if (event.key.match(/^[a-zA-Z0-9]$/)) {
          onOpenChange(true);
        }
      },
      [onOpenChange]
    );

    const handleBlur = useCallback(
      (event: FocusEvent<HTMLButtonElement>) => {
        if (!open) {
          onBlur?.(event);
        }
      },
      [onBlur, open]
    );

    // Trigger blur when the popover closes and trigger is not focused
    const handleCloseAutoFocus = useCallback(() => {
      requestAnimationFrame(() => {
        const trigger = triggerRef.current;
        if (trigger && document.activeElement !== trigger) {
          onBlur?.({
            type: 'blur',
            target: trigger,
          } as FocusEvent<HTMLButtonElement>);
        }
      });
    }, [onBlur]);

    return (
      <Popover open={open && !disabled} onOpenChange={onOpenChange}>
        <PopoverTrigger
          ref={composeRefs(ref, triggerRef)}
          className={cn(css.trigger, className)}
          disabled={disabled}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          id={field?.id ?? id}
          name={name}
          data-error={!!field?.error}
          role="combobox"
        >
          {value ? (
            <ItemLabel {...getOption(value)} />
          ) : (
            <span className={css.placeholder}>
              {placeholder ?? 'Select...'}
            </span>
          )}

          <ChevronsUpDown className={css.triggerIcon} aria-hidden />
        </PopoverTrigger>

        <ComboboxContext.Provider value={context}>
          <AnimatePresence initial={false}>
            {open && (
              <PopoverPortal
                forceMount
                container={triggerRef.current?.closest("[role='dialog']")}
              >
                <PopoverContent
                  align="center"
                  side="bottom"
                  sideOffset={5}
                  collisionPadding={10}
                  onEscapeKeyDown={() => setOpen(false)}
                  onCloseAutoFocus={handleCloseAutoFocus}
                  asChild
                >
                  <motion.div
                    className={css.content}
                    initial={{
                      opacity: 0,
                      scale: 0.99,
                      y: 'calc(var(--direction) * 3px)',
                    }}
                    animate={{
                      opacity: 1,
                      scale: 1,
                      y: 'calc(var(--direction) * 0px)',
                    }}
                    exit={{
                      opacity: 0,
                      scale: 0.99,
                      y: 'calc(var(--direction) * 3px)',
                    }}
                    transition={{ duration: 0.2, ease: easeOutSmooth }}
                  >
                    <ComboboxContent
                      search={search}
                      onSearchChange={onSearchChange}
                      shouldFilter={shouldFilter}
                      isLoadingItems={isLoadingItems}
                      items={items}
                      allowCreate={allowCreate}
                    />
                  </motion.div>
                </PopoverContent>
              </PopoverPortal>
            )}
          </AnimatePresence>
        </ComboboxContext.Provider>
      </Popover>
    );
  }
) as <T>(props: ComboboxProps<T> & { ref?: Ref<HTMLButtonElement> }) => any;
